import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { Loader, KpiCard } from "../components/KpiCard";
import { Modal, Field, TextInput, SelectInput, AddButton } from "../components/Form";
import { Switch } from "../components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { CalendarClock, Mail, Send, Loader2, Trash2, UserPlus, Users, History, Eye, EyeOff } from "lucide-react";

export default function BoardMeetings() {
  const { user } = useAuth();
  const canManage = ["admin", "finance_officer"].includes(user?.role);
  const [meetings, setMeetings] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [log, setLog] = useState([]);
  const [open, setOpen] = useState(false);
  const [recOpen, setRecOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recBusy, setRecBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingMtg, setSendingMtg] = useState("");
  const [history, setHistory] = useState(null);     // { meeting, entries }
  const [form, setForm] = useState({ title: "", meeting_date: "", location: "", notes: "" });
  const [recForm, setRecForm] = useState({ name: "", email: "", title: "Trustee", cc: false });

  const load = useCallback(() => {
    api.get("/board-meetings").then((r) => setMeetings(r.data));
    api.get("/recipients").then((r) => setRecipients(r.data)).catch(() => {});
    api.get("/email-log").then((r) => setLog(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/board-meetings", form);
      toast.success("Meeting scheduled — packet will auto-send the day before");
      setOpen(false);
      setForm({ title: "", meeting_date: "", location: "", notes: "" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/board-meetings/${id}`); toast.success("Meeting removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/send-board-packet");
      toast.success(`Packet emailed to ${data.sent.length} trustee(s)${data.cc?.length ? ` · CC ${data.cc.length}` : ""}`);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSending(false); }
  };

  const addRecipient = async (e) => {
    e.preventDefault();
    setRecBusy(true);
    try {
      await api.post("/recipients", recForm);
      toast.success("Recipient added");
      setRecOpen(false);
      setRecForm({ name: "", email: "", title: "Trustee", cc: false });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setRecBusy(false); }
  };

  const toggleRec = async (rec, field) => {
    setRecipients((prev) => prev.map((r) => (r.id === rec.id ? { ...r, [field]: !r[field] } : r)));
    try { await api.patch(`/recipients/${rec.id}`, { [field]: !rec[field] }); }
    catch (err) { toast.error(apiError(err)); load(); }
  };

  const removeRec = async (id) => {
    try { await api.delete(`/recipients/${id}`); toast.success("Recipient removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const sendMeeting = async (mtg) => {
    setSendingMtg(mtg.id);
    try {
      const { data } = await api.post(`/reports/send-board-packet?meeting_id=${mtg.id}`);
      toast.success(`Packet sent for "${mtg.title}" — ${data.sent.length} trustee(s)${data.cc?.length ? ` · CC ${data.cc.length}` : ""}`);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSendingMtg(""); }
  };

  const openHistory = async (mtg) => {
    try {
      const { data } = await api.get(`/email-log?meeting_id=${mtg.id}`);
      setHistory({ meeting: mtg, entries: data });
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!meetings) return <Loader />;

  const columns = [
    { key: "title", label: "Meeting", className: "font-medium" },
    { key: "meeting_date", label: "Date", render: (r) => fmtDate(r.meeting_date) },
    { key: "location", label: "Location", className: "text-[#5C5C54]" },
    { key: "packet_sent", label: "Packet", render: (r) => <StatusBadge tone={r.packet_sent ? "green" : "amber"}>{r.packet_sent ? "Sent" : "Auto-sends day before"}</StatusBadge> },
    { key: "actions", label: "", align: "right", render: (r) => (
      <div className="flex items-center justify-end gap-3">
        <button onClick={() => openHistory(r)} data-testid={`history-${r.id}`} title="Packet history"
          className="text-stone-400 hover:text-forest transition-colors"><History className="h-4 w-4" /></button>
        {canManage && (
          <button onClick={() => sendMeeting(r)} disabled={sendingMtg === r.id} data-testid={`send-meeting-${r.id}`}
            className="text-xs text-forest font-medium hover:underline flex items-center gap-1 disabled:opacity-60">
            {sendingMtg === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
          </button>
        )}
        {canManage && (
          <button onClick={() => remove(r.id)} data-testid={`del-meeting-${r.id}`} className="text-stone-400 hover:text-outflow transition-colors"><Trash2 className="h-4 w-4" /></button>
        )}
      </div>
    )},
  ];

  return (
    <div>
      <PageHeader title="Board Meetings" subtitle="Schedule meetings and auto-email the AI board packet to all trustees the day before."
        action={canManage && (
          <div className="flex items-center gap-3">
            <button onClick={sendNow} disabled={sending} data-testid="send-now-btn"
              className="bg-white border border-stone-300 text-stone-800 hover:bg-stone-50 rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center gap-2 disabled:opacity-60">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to Trustees Now
            </button>
            <AddButton onClick={() => setOpen(true)} label="Schedule Meeting" testid="add-meeting-btn" />
          </div>
        )} />

      <div className="grid grid-cols-3 gap-6 mb-6">
        <KpiCard label="Scheduled Meetings" value={meetings.length} icon={CalendarClock} />
        <KpiCard label="Packets Sent" value={meetings.filter((m) => m.packet_sent).length} icon={Mail} accent="#2E7D32" />
        <KpiCard label="Trustees (Enabled)" value={recipients.filter((r) => r.enabled).length} sub={`${recipients.filter((r) => r.enabled && r.cc).length} CC'd`} icon={Users} accent="#178070" />
      </div>

      {/* Trustee recipients */}
      <div className="bg-white border border-stone-200 rounded-xl p-6 mb-6" data-testid="recipients-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-medium text-[#1A1A1A]">Trustee Recipients</h2>
            <p className="text-sm text-[#5C5C54]">Toggle who receives each packet. CC recipients (e.g. the Executive Director) get a copy.</p>
          </div>
          {canManage && (
            <button onClick={() => setRecOpen(true)} data-testid="add-recipient-btn"
              className="bg-white border border-stone-300 text-stone-800 hover:bg-stone-50 rounded-md px-3.5 py-2 text-sm font-medium transition-colors duration-200 flex items-center gap-2">
              <UserPlus className="h-4 w-4" /> Add Recipient
            </button>
          )}
        </div>
        <div className="divide-y divide-stone-100">
          {recipients.length === 0 && <p className="text-sm text-[#5C5C54] py-3">No recipients yet. Add trustees to receive packets.</p>}
          {recipients.map((r) => (
            <div key={r.id} className="flex items-center gap-4 py-3" data-testid={`recipient-${r.id}`}>
              <div className="h-9 w-9 rounded-full bg-forest/10 text-forest flex items-center justify-center text-xs font-semibold">
                {r.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1A1A1A] flex items-center gap-2">
                  {r.name}
                  {r.cc && <StatusBadge tone="blue">CC</StatusBadge>}
                </div>
                <div className="text-xs text-[#5C5C54] truncate">{r.title} · {r.email}</div>
              </div>
              {canManage ? (
                <>
                  <label className="flex items-center gap-2 text-xs text-[#5C5C54]">
                    <span>Enabled</span>
                    <Switch checked={r.enabled} onCheckedChange={() => toggleRec(r, "enabled")} data-testid={`toggle-enabled-${r.id}`} />
                  </label>
                  <label className="flex items-center gap-2 text-xs text-[#5C5C54]">
                    <span>CC</span>
                    <Switch checked={r.cc} onCheckedChange={() => toggleRec(r, "cc")} data-testid={`toggle-cc-${r.id}`} />
                  </label>
                  <button onClick={() => removeRec(r.id)} data-testid={`del-recipient-${r.id}`} className="text-stone-400 hover:text-outflow transition-colors"><Trash2 className="h-4 w-4" /></button>
                </>
              ) : (
                <StatusBadge tone={r.enabled ? "green" : "gray"}>{r.enabled ? "Active" : "Off"}</StatusBadge>
              )}
            </div>
          ))}
        </div>
      </div>

      <DataTable columns={columns} rows={meetings} testid="meetings-table" empty={{ title: "No meetings scheduled", hint: "Schedule one to enable auto-send." }} />

      <div className="mt-4 text-xs text-[#5C5C54] bg-stoney border border-stone-200 rounded-lg p-4">
        <strong className="text-[#1A1A1A]">Compliance:</strong> every packet includes the foundation's mailing address and a
        one-click <span className="font-medium">unsubscribe / manage preferences</span> link. Emails currently send from a
        default managed address — verify your <span className="font-mono">davidsharmonyhouse.org</span> domain later to send under your own brand.
      </div>

      {log.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium text-[#1A1A1A] mb-3">Packet History</h2>
          <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100">
            {log.slice(0, 10).map((l) => (
              <div key={l.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <div>
                  <div className="text-[#1A1A1A]">{l.meeting_title ? <span className="font-medium">{l.meeting_title} · </span> : ""}{l.subject}</div>
                  <div className="text-xs text-[#5C5C54]">{fmtDate(l.sent_at)} · {(l.sent_to?.length || 0) + (l.cc?.length || 0)} delivered{l.failed?.length ? `, ${l.failed.length} failed` : ""}{l.cc?.length ? ` · ${l.cc.length} CC` : ""}</div>
                </div>
                <Mail className="h-4 w-4 text-forest" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-meeting packet history */}
      <Sheet open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <SheetContent className="bg-white w-full sm:max-w-lg overflow-y-auto">
          {history && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading text-2xl">{history.meeting.title}</SheetTitle>
                <SheetDescription>Packet delivery history · {fmtDate(history.meeting.meeting_date)}</SheetDescription>
              </SheetHeader>
              {canManage && (
                <button onClick={() => sendMeeting(history.meeting)} disabled={sendingMtg === history.meeting.id}
                  data-testid="sheet-send-btn"
                  className="mt-4 w-full bg-forest hover:bg-forest-900 text-white rounded-md px-4 py-2.5 text-sm font-medium transition-colors duration-200 flex items-center justify-center disabled:opacity-60">
                  {sendingMtg === history.meeting.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Send Packet for this Meeting
                </button>
              )}
              <div className="mt-6 space-y-4" data-testid="history-entries">
                {history.entries.length === 0 && <p className="text-sm text-[#5C5C54]">No packets sent for this meeting yet.</p>}
                {history.entries.map((e) => (
                  <div key={e.id} className="border border-stone-200 rounded-lg p-4">
                    <div className="text-xs text-[#5C5C54] mb-2">{fmtDate(e.sent_at)}</div>
                    <div className="space-y-1.5">
                      {(e.recipients || []).map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-[#1A1A1A]">{r.name} <span className="text-[#8A9F95] text-xs">({r.type === "cc" ? "CC" : "To"})</span></span>
                          <span className="flex items-center gap-2">
                            {r.opened
                              ? <span className="inline-flex items-center gap-1 text-xs text-inflow" title={r.opened_at || ""}><Eye className="h-3.5 w-3.5" /> Opened</span>
                              : <span className="inline-flex items-center gap-1 text-xs text-[#8A9F95]"><EyeOff className="h-3.5 w-3.5" /> Not opened</span>}
                            <StatusBadge tone={r.status === "delivered" ? "green" : "red"}>{r.status}</StatusBadge>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Modal open={open} onOpenChange={setOpen} title="Schedule Board Meeting" onSubmit={submit} busy={busy} submitLabel="Schedule" testid="meeting-modal">
        <Field label="Meeting Title" span={2}><TextInput required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Q3 Quarterly Board Meeting" /></Field>
        <Field label="Meeting Date"><TextInput type="date" required value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} /></Field>
        <Field label="Location"><TextInput value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
        <Field label="Notes" span={2}><TextInput value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
      </Modal>

      <Modal open={recOpen} onOpenChange={setRecOpen} title="Add Recipient" onSubmit={addRecipient} busy={recBusy} submitLabel="Add" testid="recipient-modal">
        <Field label="Full Name" span={2}><TextInput required value={recForm.name} onChange={(e) => setRecForm({ ...recForm, name: e.target.value })} placeholder="Jane Trustee" /></Field>
        <Field label="Email" span={2}><TextInput type="email" required value={recForm.email} onChange={(e) => setRecForm({ ...recForm, email: e.target.value })} /></Field>
        <Field label="Title / Role"><TextInput value={recForm.title} onChange={(e) => setRecForm({ ...recForm, title: e.target.value })} /></Field>
        <Field label="Delivery"><SelectInput value={String(recForm.cc)} onChange={(e) => setRecForm({ ...recForm, cc: e.target.value === "true" })} options={[{ value: "false", label: "Primary (To)" }, { value: "true", label: "CC copy" }]} /></Field>
      </Modal>
    </div>
  );
}
